// P4 purchasing (PUR-C-04): reads from the purchasing views, writes through the purchase operations (ADR-097 to
// ADR-099). Amounts, prices and costs are null for the Storekeeper (GEN-15); the screens never ask them to show.
import { Refusal } from './auth';
import { sendCommand } from './commands';
import { supabase } from './supabase';

export type LineUnit = 'box' | 'piece';
export type PoStatus = 'sent' | 'partial' | 'received' | 'closed' | 'cancelled';
export type Place = 'garage' | 'bank';
export type Delivery = 'supplier' | 'pickup';
export type PickupCategory = 'fuel' | 'toll' | 'meal' | 'other';

export interface PoLine {
  id: number;
  product_id: string;
  product: string;
  sku: string | null;
  box_capacity: number | null;
  unit: LineUnit;
  quantity: number;
  received: number;
  unit_price: number | null;
}
export interface PurchaseOrder {
  id: string;
  number: string;
  supplier_id: string;
  supplier: string;
  order_date: string;
  expected_date: string | null;
  supplier_ref: string | null;
  status: PoStatus;
  close_reason: string | null;
  created_at: string;
  late: boolean;
  ordered_pieces: number;
  received_pieces: number;
  total: string | null;
  received_value: string | null;
  lines: PoLine[];
}
export interface ReceiptLine {
  id: number;
  product_id: string;
  product: string;
  sku: string | null;
  box_capacity: number | null;
  unit: LineUnit;
  quantity: number;
  pieces: number;
  returned: number;
  unit_price: number | null;
  unit_cost: number | null;
}
export interface Receipt {
  id: string;
  number: string;
  supplier_id: string;
  supplier: string;
  po_id: string | null;
  po_number: string | null;
  business_date: string;
  at: string;
  delivery: Delivery;
  supplier_ref: string | null;
  actor_name: string | null;
  paid_from: Place | null;
  goods_total: string | null;
  pickup_total: string | null;
  total: string | null;
  lines: ReceiptLine[];
  pickup: { category: PickupCategory; amount: number }[] | null;
}
export interface SupplierReturn {
  id: string;
  number: string;
  supplier_id: string;
  supplier: string;
  receipt_number: string;
  receipt_line_id: number;
  product_id: string;
  product: string;
  box_capacity: number | null;
  unit: LineUnit;
  quantity: number;
  pieces: number;
  source: 'garage' | 'damaged';
  reason: string;
  business_date: string;
  at: string;
  actor_name: string | null;
  refund: string | null;
  refund_to: Place | null;
  value_at_cost: string | null;
  difference: string | null;
}
export interface PurchasingSummary {
  awaiting: number;
  late: number;
  receipts_month: number;
  received_month: string | null;
}
export interface ProductPurchase {
  product_id: string;
  business_date: string;
  at: string;
  number: string;
  supplier: string;
  unit: LineUnit;
  quantity: number;
  unit_price: string;
  price_per_piece: string;
  unit_cost: string;
}
export interface SupplierProduct {
  supplier_id: string;
  product_id: string;
  product: string;
  sku: string | null;
  last_date: string | null;
  last_price: string | null;
  last_unit: LineUnit;
}

function rows<T>(res: { data: unknown; error: { message: string } | null }): T[] {
  if (res.error) throw new Refusal('SERVER_ERROR');
  return (res.data ?? []) as T[];
}

export const PURCHASING = ['purchasing'] as const;

export async function loadSummary(): Promise<PurchasingSummary | null> {
  return rows<PurchasingSummary>(await supabase.from('v_purchasing_summary').select('*'))[0] ?? null;
}
/** Open orders first, then the others, newest first. */
export async function loadOrders(supplier?: string): Promise<PurchaseOrder[]> {
  let q = supabase.from('v_purchase_orders').select('*').order('created_at', { ascending: false }).limit(100);
  if (supplier) q = q.eq('supplier_id', supplier);
  const list = rows<PurchaseOrder>(await q);
  const open = (o: PurchaseOrder) => (o.status === 'sent' || o.status === 'partial' ? 0 : 1);
  return list.sort((a, b) => open(a) - open(b));
}
export async function loadReceipts(supplier?: string): Promise<Receipt[]> {
  let q = supabase
    .from('v_receipts')
    .select('*')
    .order('business_date', { ascending: false })
    .order('at', { ascending: false })
    .limit(30);
  if (supplier) q = q.eq('supplier_id', supplier);
  return rows<Receipt>(await q);
}
export async function loadReturns(supplier?: string): Promise<SupplierReturn[]> {
  let q = supabase.from('v_supplier_returns').select('*').order('at', { ascending: false }).limit(30);
  if (supplier) q = q.eq('supplier_id', supplier);
  return rows<SupplierReturn>(await q);
}
export async function loadProductPurchases(product: string): Promise<ProductPurchase[]> {
  return rows<ProductPurchase>(
    await supabase
      .from('v_product_purchases')
      .select('*')
      .eq('product_id', product)
      .order('at', { ascending: false })
      .limit(5),
  );
}
export async function loadSupplierProducts(supplier: string): Promise<SupplierProduct[]> {
  return rows<SupplierProduct>(
    await supabase.from('v_supplier_products').select('*').eq('supplier_id', supplier).order('product'),
  );
}

export interface PurchaseLineInput {
  product: string;
  unit: LineUnit;
  quantity: number;
  price: number;
  /** PRD-09: a direct receipt of a lot-tracked product carries its lot and expiry. */
  lot?: string;
  expiry?: string;
}
export interface PickupInput {
  category: PickupCategory;
  amount: number;
}
export const createOrder = (supplier: string, lines: PurchaseLineInput[], ref: string, expected: string | null) =>
  sendCommand<{ number: string }>('create_purchase', {
    p_kind: 'order',
    p_supplier: supplier,
    p_lines: lines,
    p_supplier_ref: ref || null,
    p_expected: expected,
  });
export const createDirectReceipt = (
  supplier: string,
  lines: PurchaseLineInput[],
  ref: string,
  date: string,
  delivery: Delivery,
  pickup: PickupInput[],
  paidFrom: Place,
) =>
  sendCommand<{ number: string }>('create_purchase', {
    p_kind: 'direct',
    p_supplier: supplier,
    p_lines: lines,
    p_supplier_ref: ref || null,
    p_date: date,
    p_delivery: delivery,
    p_pickup: delivery === 'pickup' ? pickup : [],
    p_paid_from: paidFrom,
  });
export const receiveOrder = (
  po: string,
  /** PRD-09: a lot-tracked line also carries its lot and expiry (YYYY-MM-DD). */
  lines: { line: number; quantity: number; lot?: string; expiry?: string }[],
  delivery: Delivery,
  pickup: PickupInput[],
  paidFrom: Place,
) =>
  sendCommand<{ number: string }>('receive_po', {
    p_po: po,
    p_lines: lines,
    p_delivery: delivery,
    p_pickup: delivery === 'pickup' ? pickup : [],
    p_paid_from: paidFrom,
  });
export const closeOrder = (po: string, reason: string) => sendCommand('close_po', { p_po: po, p_reason: reason });
export const cancelOrder = (po: string) => sendCommand('cancel_po', { p_po: po });
export const returnToSupplier = (
  line: number,
  source: 'garage' | 'damaged',
  quantity: number,
  reason: string,
  refundTo: Place,
) =>
  sendCommand<{ number: string }>('supplier_return', {
    p_receipt_line: line,
    p_source: source,
    p_quantity: quantity,
    p_reason: reason,
    p_refund_to: refundTo,
  });
